import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { signInWithPopup, getAdditionalUserInfo } from "firebase/auth";
import type { UserCredential } from "firebase/auth";
import { fetchAgencyContext, fetchUserDeletionStatus, updateUserProfile, UserDeletionStatus } from "@/lib/api";
import { retryingFetch } from "@/lib/http";
import { firebaseAuth, googleProvider } from "@/lib/firebase";
import { clearFormattingSettings } from "@/lib/format";
import {
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  LOGIN_AT_COOKIE,
  USER_EMAIL_COOKIE,
  USER_ID_COOKIE,
  USER_IMAGE_COOKIE,
  USER_NAME_COOKIE,
  USER_PROVIDER_COOKIE,
  clearSessionCookies,
  markLoginTime,
  readCookie,
  writeSessionCookies,
} from "@/lib/session";

// Firebase ID tokens expire 60 minutes after mint with no sliding renewal, so an open
// tab needs a proactive refresh well before that or the next API call 401s the user out.
const TOKEN_REFRESH_INTERVAL_MS = 45 * 60 * 1000;
// Industry-standard pair for finance-adjacent apps: a hard cap regardless of activity,
// plus a shorter idle cap that fires even if the tab is left open unattended.
const ABSOLUTE_SESSION_MS = 24 * 60 * 60 * 1000;
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"] as const;

let lastActivityAt = Date.now();
function markActivity(): void {
  lastActivityAt = Date.now();
}

// computeSessionExpired is the pure decision behind isSessionExpired, split out so the
// absolute-cap/idle-cap logic is unit-testable without faking cookies or timers.
export function computeSessionExpired(loginAtMs: number | null, lastActivityAtMs: number, nowMs: number): boolean {
  if (loginAtMs && nowMs - loginAtMs > ABSOLUTE_SESSION_MS) return true;
  return nowMs - lastActivityAtMs > IDLE_TIMEOUT_MS;
}

function isSessionExpired(): boolean {
  const loginAt = Number(readCookie(LOGIN_AT_COOKIE)) || null;
  return computeSessionExpired(loginAt, lastActivityAt, Date.now());
}

// forceSignOut mirrors api.ts's 401 handler: clear client state and hard-navigate to
// /login. The navigation itself resets React state, so there's nothing else to do here.
function forceSignOut(): void {
  clearSessionCookies();
  clearFormattingSettings();
  window.location.href = "/login";
}

export interface AgencyContext {
  agency_id: string;
  agency_name: string;
  member_count: number;
  tier: "FREE" | "PRO" | "PREMIUM";
  subscription_status: "trial" | "trial_expired" | "monthly" | "yearly" | "beta" | "expired";
  trial_ends_at?: string;
  agency_role: "owner" | "member";
  user_role: "user" | "admin";
}

interface User {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string | null;
  provider: "google" | "email";
}

interface AuthState {
  user: User | null;
  agencyContext: AgencyContext | null;
  deletionStatus: UserDeletionStatus | null;
  loading: boolean;
  notProvisioned: boolean;
  pendingConsent: boolean;
  pendingConsentIsNewUser: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string, name: string) => Promise<void>;
  resendEmailVerification: (email: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  confirmAcceptance: () => Promise<void>;
  declineConsent: () => Promise<void>;
  refreshAgencyContext: () => Promise<void>;
  refreshDeletionStatus: () => Promise<void>;
  updateProfileName: (name: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function readUserFromCookies(): User | null {
  const uid = readCookie(USER_ID_COOKIE);
  if (!uid) return null;
  const rawProvider = readCookie(USER_PROVIDER_COOKIE);
  return {
    uid,
    email: readCookie(USER_EMAIL_COOKIE),
    displayName: readCookie(USER_NAME_COOKIE),
    photoURL: readCookie(USER_IMAGE_COOKIE) || null,
    provider: rawProvider === "google" ? "google" : "email",
  };
}

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8080";
const FIREBASE_API_KEY = import.meta.env.VITE_FIREBASE_WEB_API_KEY ?? "";


type FirebaseAuthSuccess = {
  idToken: string;
  refreshToken?: string;
  localId: string;
  email: string;
  displayName?: string;
  photoUrl?: string;
};

type FirebaseLookupUser = {
  localId?: string;
  email?: string;
  displayName?: string;
  photoUrl?: string;
  emailVerified?: boolean;
};

type FirebaseRestErrorBody = {
  error?: string | {
    message?: string;
  };
  message?: string;
};

function firebaseRestErrorMessage(body: FirebaseRestErrorBody, fallback: string) {
  if (typeof body.error === "string") return body.error;
  return body.error?.message || body.message || fallback;
}

function logFirebaseRestError(endpoint: string, status: number, message: string) {
  console.error(`[firebase] ${endpoint} failed`, { status, message });
}

async function firebaseAuthRequest(endpoint: string, payload: Record<string, unknown>): Promise<FirebaseAuthSuccess> {
  if (!FIREBASE_API_KEY) {
    throw new Error("Missing VITE_FIREBASE_WEB_API_KEY");
  }
  const res = await fetch(`https://identitytoolkit.googleapis.com/v1/${endpoint}?key=${FIREBASE_API_KEY}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = (await res.json()) as FirebaseRestErrorBody & FirebaseAuthSuccess;
  if (!res.ok) {
    const message = firebaseRestErrorMessage(body, "Authentication failed");
    logFirebaseRestError(endpoint, res.status, message);
    throw new Error(message);
  }
  return body;
}

async function syncUser(accessToken: string, user: { id: string; email: string; name: string; image?: string | null; emailVerified: boolean }): Promise<{ needs_acceptance: boolean }> {
  const res = await retryingFetch(`${API_URL}/auth/sync`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      id: user.id,
      email: user.email,
      name: user.name,
      image: user.image ?? "",
      emailVerified: user.emailVerified,
    }),
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(payload.error || payload.message || "Failed to sync user");
  }
  const data = (await res.json()) as { needs_acceptance?: boolean };
  return { needs_acceptance: data.needs_acceptance ?? false };
}

async function callLogoutAPI(accessToken: string): Promise<void> {
  await retryingFetch(`${API_URL}/auth/logout`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

async function callConfirmAcceptanceAPI(accessToken: string): Promise<void> {
  const res = await retryingFetch(`${API_URL}/auth/confirm-acceptance`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error("Failed to record acceptance");
  }
}

async function ensureEmailVerified(idToken: string) {
  if (!FIREBASE_API_KEY) return;
  const res = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${FIREBASE_API_KEY}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as FirebaseRestErrorBody;
    const message = firebaseRestErrorMessage(body, "Unable to verify email status");
    logFirebaseRestError("accounts:lookup", res.status, message);
    throw new Error(message);
  }
  const body = (await res.json()) as { users?: Array<{ emailVerified?: boolean }> };
  if (!body.users?.[0]?.emailVerified) {
    throw new Error("Email not verified");
  }
}

async function sendVerificationEmail(idToken: string) {
  const res = await retryingFetch(`${API_URL}/auth/email-verification`, {
    method: "POST",
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as FirebaseRestErrorBody;
    throw new Error(firebaseRestErrorMessage(body, "Failed to send verification email"));
  }
}

async function lookupFirebaseUser(idToken: string): Promise<FirebaseLookupUser | null> {
  if (!FIREBASE_API_KEY) return null;
  const res = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${FIREBASE_API_KEY}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as FirebaseRestErrorBody;
    logFirebaseRestError("accounts:lookup", res.status, firebaseRestErrorMessage(body, "Unable to lookup Firebase user"));
    return null;
  }
  const body = (await res.json()) as { users?: FirebaseLookupUser[] };
  return body.users?.[0] ?? null;
}

async function updateFirebaseDisplayName(idToken: string, displayName: string): Promise<FirebaseAuthSuccess> {
  return firebaseAuthRequest("accounts:update", {
    idToken,
    displayName,
    returnSecureToken: true,
  });
}

async function updateFirebasePassword(idToken: string, password: string): Promise<FirebaseAuthSuccess> {
  return firebaseAuthRequest("accounts:update", {
    idToken,
    password,
    returnSecureToken: true,
  });
}

// refreshIdToken exchanges the stored refresh token for a fresh ID token via Firebase's
// Secure Token API, so an open dashboard tab survives past the 60-minute ID token lifetime.
async function refreshIdToken(): Promise<void> {
  const refreshToken = readCookie(REFRESH_TOKEN_COOKIE);
  if (!refreshToken || !FIREBASE_API_KEY) return;

  const current = readUserFromCookies();
  if (!current) return;

  if (isSessionExpired()) {
    forceSignOut();
    return;
  }

  try {
    const res = await fetch(`https://securetoken.googleapis.com/v1/token?key=${FIREBASE_API_KEY}`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ grant_type: "refresh_token", refresh_token: refreshToken }),
    });
    if (!res.ok) return;
    const data = (await res.json()) as { id_token?: string; refresh_token?: string };
    if (!data.id_token) return;

    writeSessionCookies({
      accessToken: data.id_token,
      refreshToken: data.refresh_token ?? refreshToken,
      userID: current.uid,
      email: current.email,
      name: current.displayName,
      image: current.photoURL ?? "",
      provider: current.provider,
    });
  } catch {
    // Best-effort; the next API call will 401 and redirect to /login if the session is truly dead.
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [agencyContext, setAgencyContext] = useState<AgencyContext | null>(null);
  const [deletionStatus, setDeletionStatus] = useState<UserDeletionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [notProvisioned, setNotProvisioned] = useState(false);
  // Pending credential for new Google users who need to confirm before sign-in completes
  const [pendingGoogleCred, setPendingGoogleCred] = useState<UserCredential | null>(null);
  // True for existing users who signed up before this feature and haven't confirmed yet
  const [needsAcceptance, setNeedsAcceptance] = useState(false);

  const pendingConsent = !!pendingGoogleCred || needsAcceptance;
  const pendingConsentIsNewUser = !!pendingGoogleCred;

  const refreshAgencyContext = async () => {
    const nextUser = readUserFromCookies();
    setUser(nextUser);
    if (!nextUser) {
      setAgencyContext(null);
      setNotProvisioned(false);
      return;
    }
    try {
      const ctx = await fetchAgencyContext();
      setAgencyContext(ctx);
      setNotProvisioned(false);
    } catch (err) {
      setAgencyContext(null);
      setNotProvisioned(err instanceof Error && err.message === "not_provisioned");
    }
  };

  const refreshDeletionStatus = async () => {
    const nextUser = readUserFromCookies();
    if (!nextUser) {
      setDeletionStatus(null);
      return;
    }
    try {
      const status = await fetchUserDeletionStatus();
      setDeletionStatus(status);
    } catch {
      setDeletionStatus(null);
    }
  };

  useEffect(() => {
    markActivity();
    void (async () => {
      await refreshIdToken();
      await refreshAgencyContext();
      await refreshDeletionStatus();
      setLoading(false);
    })();

    const refreshInterval = setInterval(() => {
      void refreshIdToken();
    }, TOKEN_REFRESH_INTERVAL_MS);
    const idleCheckInterval = setInterval(() => {
      if (readCookie(ACCESS_TOKEN_COOKIE) && isSessionExpired()) forceSignOut();
    }, IDLE_CHECK_INTERVAL_MS);
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, markActivity, { passive: true });
    }

    return () => {
      clearInterval(refreshInterval);
      clearInterval(idleCheckInterval);
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, markActivity);
      }
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      agencyContext,
      deletionStatus,
      loading,
      notProvisioned,
      pendingConsent,
      pendingConsentIsNewUser,

      signInWithGoogle: async () => {
        const credential = await signInWithPopup(firebaseAuth, googleProvider);
        const additionalInfo = getAdditionalUserInfo(credential);

        if (additionalInfo?.isNewUser) {
          // New user: hold the credential and require consent before completing sign-in
          setPendingGoogleCred(credential);
          return;
        }

        // Existing Google user: proceed normally
        const idToken = await credential.user.getIdToken();
        const resolvedEmail = credential.user.email;
        if (!resolvedEmail) throw new Error("Google account email is required");
        const resolvedName = credential.user.displayName || resolvedEmail;
        const resolvedPhoto = credential.user.photoURL ?? "";
        writeSessionCookies({
          accessToken: idToken,
          refreshToken: credential.user.refreshToken,
          userID: credential.user.uid,
          email: resolvedEmail,
          name: resolvedName,
          image: resolvedPhoto,
          provider: "google",
        });
        markLoginTime();
        const { needs_acceptance } = await syncUser(idToken, {
          id: credential.user.uid,
          email: resolvedEmail,
          name: resolvedName,
          image: resolvedPhoto,
          emailVerified: credential.user.emailVerified,
        });
        if (needs_acceptance) setNeedsAcceptance(true);
        sessionStorage.setItem("dashboard_force_refresh", "1");
        await refreshAgencyContext();
        await refreshDeletionStatus();
      },

      signInWithEmail: async (email: string, password: string) => {
        let auth: FirebaseAuthSuccess;
        try {
          auth = await firebaseAuthRequest("accounts:signInWithPassword", {
            email,
            password,
            returnSecureToken: true,
          });
        } catch (err: unknown) {
          // Normalize all Firebase credential errors to a single generic message to prevent
          // user enumeration (INVALID_PASSWORD, EMAIL_NOT_FOUND, INVALID_LOGIN_CREDENTIALS, etc.).
          const msg = err instanceof Error ? err.message : "";
          const credentialErrorCodes = [
            "INVALID_PASSWORD",
            "EMAIL_NOT_FOUND",
            "INVALID_LOGIN_CREDENTIALS",
            "USER_DISABLED",
          ];
          if (credentialErrorCodes.some((code) => msg.includes(code))) {
            throw new Error("Username or password is incorrect.");
          }
          throw err;
        }
        await ensureEmailVerified(auth.idToken);
        const lookedUp = await lookupFirebaseUser(auth.idToken);
        const name = lookedUp?.displayName || auth.displayName || lookedUp?.email || auth.email;
        const photo = lookedUp?.photoUrl || auth.photoUrl || "";
        writeSessionCookies({
          accessToken: auth.idToken,
          refreshToken: auth.refreshToken,
          userID: auth.localId,
          email: auth.email,
          name,
          image: photo,
          provider: "email",
        });
        markLoginTime();
        const { needs_acceptance } = await syncUser(auth.idToken, {
          id: auth.localId,
          email: auth.email,
          name,
          image: photo,
          emailVerified: lookedUp?.emailVerified ?? false,
        });
        if (needs_acceptance) setNeedsAcceptance(true);
        sessionStorage.setItem("dashboard_force_refresh", "1");
        await refreshAgencyContext();
        await refreshDeletionStatus();
      },

      registerWithEmail: async (email: string, password: string, name: string) => {
        const signedUp = await firebaseAuthRequest("accounts:signUp", {
          email,
          password,
          returnSecureToken: true,
        });

        const desiredName = name.trim();
        const auth = desiredName
          ? await updateFirebaseDisplayName(signedUp.idToken, desiredName)
          : signedUp;

        const idToken = auth.idToken || signedUp.idToken;
        const localId = auth.localId || signedUp.localId;
        const resolvedEmail = auth.email || signedUp.email;
        const displayName = auth.displayName || desiredName || resolvedEmail;
        const lookedUp = await lookupFirebaseUser(idToken);
        await syncUser(idToken, {
          id: localId,
          email: resolvedEmail,
          name: displayName,
          image: auth.photoUrl ?? "",
          emailVerified: lookedUp?.emailVerified ?? false,
        });
        // Consent was given by the required checkbox on the signup form
        await callConfirmAcceptanceAPI(idToken);
        await sendVerificationEmail(idToken);
        await refreshDeletionStatus();
      },

      confirmAcceptance: async () => {
        if (pendingGoogleCred) {
          // Complete sign-in for a new Google user who just consented
          const idToken = await pendingGoogleCred.user.getIdToken();
          const resolvedEmail = pendingGoogleCred.user.email;
          if (!resolvedEmail) throw new Error("Google account email is required");
          const resolvedName = pendingGoogleCred.user.displayName || resolvedEmail;
          const resolvedPhoto = pendingGoogleCred.user.photoURL ?? "";
          writeSessionCookies({
            accessToken: idToken,
            refreshToken: pendingGoogleCred.user.refreshToken,
            userID: pendingGoogleCred.user.uid,
            email: resolvedEmail,
            name: resolvedName,
            image: resolvedPhoto,
            provider: "google",
          });
          markLoginTime();
          await syncUser(idToken, {
            id: pendingGoogleCred.user.uid,
            email: resolvedEmail,
            name: resolvedName,
            image: resolvedPhoto,
            emailVerified: pendingGoogleCred.user.emailVerified,
          });
          await callConfirmAcceptanceAPI(idToken);
          setPendingGoogleCred(null);
          sessionStorage.setItem("dashboard_force_refresh", "1");
          await refreshAgencyContext();
          await refreshDeletionStatus();
        } else {
          // Existing user confirming on their next login
          const token = readCookie(ACCESS_TOKEN_COOKIE);
          if (!token) throw new Error("Not authenticated");
          await callConfirmAcceptanceAPI(token);
          setNeedsAcceptance(false);
        }
      },

      declineConsent: async () => {
        if (pendingGoogleCred) {
          // New Google user declined - delete the Firebase account that was just created
          try {
            await pendingGoogleCred.user.delete();
          } catch {
            // Best-effort; the account may already be gone
          }
          setPendingGoogleCred(null);
        } else {
          // Existing user declined - sign them out
          clearSessionCookies();
          setUser(null);
          setAgencyContext(null);
          setNeedsAcceptance(false);
          window.location.href = "/login";
        }
      },

      resendEmailVerification: async (email: string, password: string) => {
        const auth = await firebaseAuthRequest("accounts:signInWithPassword", {
          email,
          password,
          returnSecureToken: true,
        });
        await sendVerificationEmail(auth.idToken);
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        const currentUser = readUserFromCookies();
        if (!currentUser?.email) {
          throw new Error("Authenticated email is missing");
        }

        const reauth = await firebaseAuthRequest("accounts:signInWithPassword", {
          email: currentUser.email,
          password: currentPassword,
          returnSecureToken: true,
        });
        const updated = await updateFirebasePassword(reauth.idToken, newPassword);
        const lookedUp = await lookupFirebaseUser(updated.idToken);
        const name = lookedUp?.displayName || currentUser.displayName || updated.displayName || updated.email;
        const photo = lookedUp?.photoUrl || currentUser.photoURL || updated.photoUrl || "";
        writeSessionCookies({
          accessToken: updated.idToken,
          refreshToken: updated.refreshToken,
          userID: updated.localId,
          email: updated.email,
          name,
          image: photo,
          provider: "email",
        });
        await syncUser(updated.idToken, {
          id: updated.localId,
          email: updated.email,
          name,
          image: photo,
          emailVerified: lookedUp?.emailVerified ?? false,
        });
        await refreshAgencyContext();
        await refreshDeletionStatus();
      },

      signOut: async () => {
        const accessToken = readCookie(ACCESS_TOKEN_COOKIE);
        if (accessToken) {
          callLogoutAPI(accessToken).catch(() => {});
        }
        clearSessionCookies();
        clearFormattingSettings();
        setUser(null);
        setAgencyContext(null);
        setDeletionStatus(null);
        setPendingGoogleCred(null);
        setNeedsAcceptance(false);
        window.location.href = "/login";
      },

      // Updates the display name everywhere it's read: the backend DB, the
      // Firebase account (so it survives logout/login), the session cookie, and
      // the in-memory user (so the UI updates live).
      updateProfileName: async (name: string) => {
        await updateUserProfile(name);

        let token = readCookie(ACCESS_TOKEN_COOKIE);
        let refreshToken = readCookie(REFRESH_TOKEN_COOKIE);
        if (token) {
          const refreshed = await updateFirebaseDisplayName(token, name);
          if (refreshed.idToken) token = refreshed.idToken;
          if (refreshed.refreshToken) refreshToken = refreshed.refreshToken;
        }

        const current = readUserFromCookies();
        writeSessionCookies({
          accessToken: token,
          refreshToken,
          userID: current?.uid ?? "",
          email: current?.email ?? "",
          name,
          image: current?.photoURL ?? "",
          provider: current?.provider,
        });
        setUser((prev) => (prev ? { ...prev, displayName: name } : prev));
      },

      refreshAgencyContext,
      refreshDeletionStatus,
    }),
    [agencyContext, deletionStatus, loading, notProvisioned, pendingConsent, pendingConsentIsNewUser, pendingGoogleCred, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
